import { useState, useContext, useEffect } from 'react';
import { FormikProps } from 'formik';
import { Box, Paper, Typography, IconButton, Link } from '@mui/material';
import ArrowLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowRightIcon from '@mui/icons-material/ChevronRight';
import { IFormikValuesForRuleAndOverridesPerSetting, IFormikValuesForUPCRuleAndOverridesPerSetting, IIneligibleSettingAPI, IIneligibleSettingDetail, IIneligibleSettingsContext } from '../../../interfaces/ineligibleSettingInterface';
import { IneligibleSettingsContext } from '../../../context/ineligibleSettingsContext';
import { putAltDescriptionForAllCollaterals, putInvAltDescriptionForAllCollaterals } from '../../../api/ineligible-settings';
import { NON_EXISTING } from '../../../utility/constants';
import DraggableList, { DraggableListItem } from '../draggable-list';
import { ApplyAltDescriptionModal } from '../modals/apply-alt-description';
import styles from './styles';
import _ from 'lodash';

interface IIneligibleListProps {
  formik: FormikProps<IFormikValuesForRuleAndOverridesPerSetting> | FormikProps<IFormikValuesForUPCRuleAndOverridesPerSetting>;
}

/**
 * Component represents the IneligibleList component.
 * @param props The component's props.
 * @returns The IneligibleList component.
 */
const IneligibleList: React.FC<IIneligibleListProps> = (props) => {
  const {
    ineligibleSettingDetails,
    fetchedIneligibleSettingDetails,
    setSelectedIneligibleIndex,
    ineligibleListContainerRef,
    isHidingInactiveIneligibles,
    hideInactiveIneligiblesToggle, setHideInactiveIneligiblesToggle,
  }                                                               = useContext(IneligibleSettingsContext) as IIneligibleSettingsContext;

  const [isDrawerOpen, setIsDrawerOpen]                           = useState<boolean>(true);
  const [pendingChange, setPendingChange]                         = useState<{ item: DraggableListItem; newValue: string | null } | null>(null);

  const [draggableListItems, setDraggableListItems]               = useState<DraggableListItem[]>(props.formik.values.ineligibleSettingDetails.map(({ ineligibleSetting }) => ({
                                                                      recordId: ineligibleSetting.ineligibleSettingId,
                                                                      name: ineligibleSetting.altDescription ?? ineligibleSetting.description,
                                                                      defaultName: ineligibleSetting.description,
                                                                      value: ineligibleSetting.code,
                                                                      disabled: ineligibleSetting.disabled,
                                                                      isRenamed: (ineligibleSetting.altDescription ?? ineligibleSetting.description) !== ineligibleSetting.description
                                                                    })));

  /**
   * This hook updates the draggable list items with values fetched from the backend.
   */
  useEffect(() => {
    setDraggableListItems(
      ineligibleSettingDetails.map(({ ineligibleSetting }) => ({
        recordId: ineligibleSetting.ineligibleSettingId,
        name: ineligibleSetting.altDescription ?? ineligibleSetting.description,
        defaultName: ineligibleSetting.description,
        value: ineligibleSetting.code,
        disabled: ineligibleSetting.disabled,
        isRenamed: (ineligibleSetting.altDescription ?? ineligibleSetting.description) !== ineligibleSetting.description
      }))
    );
  }, [ineligibleSettingDetails]);

  /**
   * This hook updates the draggable list items based on formik resets.
   */
  useEffect(() => {
    setDraggableListItems(
      props.formik.values.ineligibleSettingDetails.map(({ ineligibleSetting }) => ({
        recordId: ineligibleSetting.ineligibleSettingId,
        name: ineligibleSetting.altDescription ?? ineligibleSetting.description,
        defaultName: ineligibleSetting.description,
        value: ineligibleSetting.code,
        disabled: ineligibleSetting.disabled,
        isRenamed: (ineligibleSetting.altDescription ?? ineligibleSetting.description) !== ineligibleSetting.description
      }))
    );
  }, [props.formik.values.ineligibleSettingDetails]);

  /**
   * This function toggles the visibility of the Ineligible Settings panel.
   */
  const toggleSettingsOpen = () => setIsDrawerOpen(!isDrawerOpen);

  /**
   * This function handles the drop of an item in the draggable list.
   * @param draggedListItem The dragged list item.
   */
  const handleItemDrop = async (draggedListItem: DraggableListItem) => {
    /** reorders formik values & edits each settings' order based on dragged item */
    const ineligibleSettingDetails = _.cloneDeep(props.formik.values.ineligibleSettingDetails);
    draggableListItems.forEach((draggedItem, draggedIndex) => {
      const formikDetail = ineligibleSettingDetails.find(detail => detail.ineligibleSetting.ineligibleSettingId === draggedItem.recordId) as NonNullable<IIneligibleSettingDetail>;
      formikDetail.ineligibleSetting.order = draggedIndex + 1;
      props.formik.setFieldValue(`ineligibleSettingDetails[${draggedIndex}]`, formikDetail);
      if (draggedItem.disabled) { return; }
      draggedListItem.recordId === draggedItem.recordId && setSelectedIneligibleIndex(draggedIndex);
    });

    const draggedListItemIndex = draggableListItems.findIndex(item => item.recordId === draggedListItem.recordId);
    if (!draggedListItem.disabled) { setSelectedIneligibleIndex(draggedListItemIndex); return; }
    const detailIndexToSelect = props.formik.values.ineligibleSettingDetails.findIndex(detail => !detail.ineligibleSetting.disabled && detail.ineligibleSetting.ineligibleSettingId !== draggedListItem.recordId);
    setSelectedIneligibleIndex(detailIndexToSelect); /** auto select another non-disabled setting if dragged setting is disabled */
  };

  /**
   * This function handles the toggling of an item in the draggable list.
   * @param listItem The list item to toggle.
   */
  const handleItemToggle = async (listItem: DraggableListItem) => {
    const toggledListItem = { ...listItem, disabled: !listItem.disabled } as DraggableListItem;
    const detailSettingIndex = draggableListItems.findIndex(item => item.recordId === toggledListItem.recordId);
    /** set changed ineligible setting's disabled state in formik */
    props.formik.setFieldValue(`ineligibleSettingDetails[${detailSettingIndex}].ineligibleSetting.disabled`, toggledListItem.disabled);

    if (!toggledListItem.disabled) {
      setSelectedIneligibleIndex(detailSettingIndex);
      /** update draggable list items to sync with formik values */
      setDraggableListItems(draggableListItems.map(item => item.recordId !== listItem.recordId ? item : toggledListItem));
      return;
    }
    /** to reset details after disabling ineligible*/
    const originalSettingIndex = fetchedIneligibleSettingDetails.findIndex(item => item.ineligibleSetting.ineligibleSettingId === toggledListItem.recordId);
    props.formik.setFieldValue(`ineligibleSettingDetails[${detailSettingIndex}].ineligibleRule`, fetchedIneligibleSettingDetails[originalSettingIndex]?.ineligibleRule);
    props.formik.setFieldValue(`ineligibleSettingDetails[${detailSettingIndex}].ineligibleRuleOverrides`, fetchedIneligibleSettingDetails[originalSettingIndex]?.ineligibleRuleOverrides);
    /** to reset validation errors after disabling ineligible */
    props.formik.setFieldError(`ineligibleSettingDetails[${detailSettingIndex}]`, undefined);
    const detailIndexToSelect = props.formik.values.ineligibleSettingDetails.findIndex(detail => !detail.ineligibleSetting.disabled && detail.ineligibleSetting.ineligibleSettingId !== toggledListItem.recordId);
    setSelectedIneligibleIndex(detailIndexToSelect); /** auto select another non-disabled setting if toggled setting is disabled */
    /** update draggable list items to sync with formik values */
    setDraggableListItems(draggableListItems.map(item => item.recordId !== listItem.recordId ? item : toggledListItem));
  };

  /**
   * This function sets the given item's altDescription in formik and syncs the draggable list
   * item's display fields to match, without calling any API directly (persistence happens via
   * the normal page-level Save flow, matching how toggle/reorder already work).
   * @param item The list item to update.
   * @param newValue The new altDescription, or null to clear it.
   */
  const applyAltDescriptionToRow = (item: DraggableListItem, newValue: string | null) => {
    const detailIndex = props.formik.values.ineligibleSettingDetails.findIndex(detail => detail.ineligibleSetting.ineligibleSettingId === item.recordId);
    if (detailIndex === NON_EXISTING) { return; }
    props.formik.setFieldValue(`ineligibleSettingDetails[${detailIndex}].ineligibleSetting.altDescription`, newValue);
    setDraggableListItems(draggableListItems.map(listItem => {
      if (listItem.recordId !== item.recordId) { return listItem; }
      const name = newValue ?? listItem.defaultName;
      return { ...listItem, name, isRenamed: name !== listItem.defaultName };
    }));
  };

  /**
   * This function applies an altDescription change for a row: directly for the UPC/parent flow
   * (no arCollateralId or invCollateralId, so nothing to apply to "all collaterals" against), or
   * via the scope-confirmation modal for the regular per-AR-collateral or per-Inventory-collateral
   * flow.
   * @param item The list item being renamed or reset.
   * @param newValue The new altDescription, or null to reset it.
   */
  const openScopeModal = (item: DraggableListItem, newValue: string | null) => {
    const detail = props.formik.values.ineligibleSettingDetails.find(detail => detail.ineligibleSetting.ineligibleSettingId === item.recordId);
    if (detail === undefined) { return; }
    const arCollateralId = (detail.ineligibleSetting as IIneligibleSettingAPI).arCollateralId;
    const invCollateralId = detail.ineligibleSetting.invCollateralId;
    if ((arCollateralId === undefined || arCollateralId === null) && (invCollateralId === undefined || invCollateralId === null)) {
      applyAltDescriptionToRow(item, newValue);
      return;
    }
    setPendingChange({ item, newValue });
  };

  /**
   * This function handles the commit of an inline rename.
   * @param item The list item that was renamed.
   * @param newName The new (trimmed, non-empty) name.
   */
  const handleRenameCommit = (item: DraggableListItem, newName: string) => openScopeModal(item, newName);

  /**
   * This function handles a click on the reset-to-default icon.
   * @param item The list item to reset.
   */
  const handleResetClick = (item: DraggableListItem) => openScopeModal(item, null);

  /**
   * This function resolves the pending scope-confirmation modal for "this collateral only".
   */
  const handleApplyToThisOnly = () => {
    if (pendingChange === null) { return; }
    applyAltDescriptionToRow(pendingChange.item, pendingChange.newValue);
    setPendingChange(null);
  };

  /**
   * This function resolves the pending scope-confirmation modal for "apply to all". Unlike "this
   * collateral only", the other collaterals under this client aren't part of the currently loaded
   * formik state, so this calls the bulk-update endpoint immediately rather than deferring to Save.
   * Which endpoint depends on whether the row is an AR or Inventory collateral setting.
   */
  const handleApplyToAll = async () => {
    if (pendingChange === null) { return; }
    const detail = props.formik.values.ineligibleSettingDetails.find(detail => detail.ineligibleSetting.ineligibleSettingId === pendingChange.item.recordId);
    if (detail === undefined) { setPendingChange(null); return; }
    const arCollateralId = (detail.ineligibleSetting as IIneligibleSettingAPI).arCollateralId;
    const applyToAllFn = (arCollateralId === undefined || arCollateralId === null) ? putInvAltDescriptionForAllCollaterals : putAltDescriptionForAllCollaterals;
    await applyToAllFn(detail.ineligibleSetting.borrowerId, detail.ineligibleSetting.code, pendingChange.newValue);
    applyAltDescriptionToRow(pendingChange.item, pendingChange.newValue);
    setPendingChange(null);
  };

  return (
    <Paper sx={isDrawerOpen ? styles.paperStyle : styles.closedPaperStyle} ref={ineligibleListContainerRef}>
      { isDrawerOpen ? (
        <>
          <Box sx={styles.titleBox}>
            <Box sx={styles.typographyBox}>
              <Box sx={styles.ineligiblesHeadingContainer}>
                <Typography tabIndex={0} variant='h6' component='h3' sx={styles.titleText}>Ineligibles</Typography>
                <Link component='button' type='button' sx={styles.hideInactiveIneligiblesButton} onClick={() => setHideInactiveIneligiblesToggle(!hideInactiveIneligiblesToggle)}>
                  { isHidingInactiveIneligibles ? 'Show Inactive' : 'Hide Inactive' }
                </Link>
              </Box>
              <Typography tabIndex={0} variant='subtitle1' sx={styles.subtitleText}>Drag and drop to rearrange columns.</Typography>
            </Box>
            <IconButton
              aria-label='Collapse icon'
              onClick={() => toggleSettingsOpen()}
              sx={styles.icon}
            >
              <ArrowLeftIcon />
            </IconButton>
          </Box>
          <DraggableList
            formik={props.formik}
            draggableListItems={draggableListItems}
            setDraggableListItems={setDraggableListItems}
            onItemToggle={handleItemToggle}
            onItemDrop={handleItemDrop}
            onRenameCommit={handleRenameCommit}
            onResetClick={handleResetClick}
          />
        </>
      ) : (
        <IconButton
          aria-label='Expand icon'
          onClick={() => toggleSettingsOpen()}
          sx={styles.overflowIcon}
        >
          <ArrowRightIcon />
        </IconButton>
      )}
      <ApplyAltDescriptionModal
        isOpen={pendingChange !== null}
        itemName={pendingChange?.item.name ?? ''}
        newValue={pendingChange?.newValue ?? null}
        handleClose={() => setPendingChange(null)}
        handleApplyToThisOnly={handleApplyToThisOnly}
        handleApplyToAll={handleApplyToAll}
      />
    </Paper>
  )
};

export default IneligibleList;
